import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import type { ILock } from "@/domains/lock"
import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { increaseLockAmountInteraction } from "@/domains/interactions"
import { IncreaseLockAmountActionsAdapter } from "@/domains/interactions/increase-lock-amount/increase-lock-amount-actions-adapter"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { IncreaseAmountCard } from "@/domains/lock-page/components/card/increase-amount-card/components/increase-amount-card"
import { TargetAmountCard } from "@/domains/lock-page/components/card/increase-amount-card/components/target-amount-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useManageLockLens } from "@/domains/on-chain"
import { SCALE, VESTED } from "@/domains/tokens/static-tokens"
import { tokenStoreToToken } from "@/domains/tokens/utils/token-store-to-token"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import useEventEmitter from "@/hooks/useEventEmitter"

export const IncreaseLockAmount = observer(() => {
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()
  const [amount, setAmount] = useState("")
  const [debouncedAmount, setDebouncedAmount] = useState("")
  const [mode, setMode] = useState<"increase" | "target">("increase")
  const breadcrumbs = makeBreadcrumbs()

  // Debounce amount for error validation (400ms delay)
  // This prevents showing errors while user is still typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedAmount(amount)
    }, 400)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [amount])

  // Hooks must be called before any conditional returns
  useSyncLockIdFromRouter()

  // Use SCALE and VESTED directly from static tokens (no useMemo needed)
  const governanceToken = SCALE
  // Convert VESTED TokenStore to Token type for useManageLockLens
  const vestedToken = tokenStoreToToken(VESTED)
  const nft = scaleApp.Pages.Lock.selectedLock
  const nftId = nft?.id

  // useManageLockLens must be called before conditional returns
  const manageLockLens = useManageLockLens({
    nftId: nftId ?? 0,
    config,
    chainId: chainConfig.chainId,
    account: account.address,
    vestedToken,
    voterAddress: chainConfig.equalizer.contracts.voter,
    governanceToken,
  })

  // Calculate amountToAddWei based on mode using BigInt precision
  // Use lockAmountRaw directly from RPC for exact precision (no string conversion)
  let amountToAddWei = 0n
  if (governanceToken && amount && Number(amount) > 0) {
    try {
      if (mode === "increase") {
        amountToAddWei = governanceToken.parseUnits(amount)
      } else {
        // Target mode: calculate difference using raw BigInt
        const targetAmountWei = governanceToken.parseUnits(amount)
        const currentLockAmountWei = nft?.lockAmountRaw ?? 0n
        // Allow target >= current (if equal, amountToAddWei will be 0n, which is valid)
        amountToAddWei = targetAmountWei >= currentLockAmountWei ? targetAmountWei - currentLockAmountWei : 0n
      }
    } catch {
      amountToAddWei = 0n
    }
  }

  // Calculate display strings for visualizer
  let amountToAdd = "0"
  if (governanceToken && amountToAddWei > 0n) {
    amountToAdd = governanceToken.formatUnits(amountToAddWei)
  }

  const targetAmount = mode === "target" ? amount : "0"

  // Calculate potential increased NFT
  let potentialIncreasedNft = scaleApp.Lock.Factory.createDefault()
  if (nft && governanceToken) {
    if (amountToAddWei === 0n) {
      potentialIncreasedNft = scaleApp.Lock.Factory.createFromNft(nft)
    } else {
      // Use lockAmountRaw directly for exact BigInt precision
      const currentLockAmountWei = nft.lockAmountRaw
      const newLockAmountWei = currentLockAmountWei + amountToAddWei
      const newLockAmount = Number(governanceToken.formatUnits(newLockAmountWei))

      const currentPowerRatio = Number(nft.lockAmount) > 0 ? Number(nft.lockValue) / Number(nft.lockAmount) : 0
      const newLockValue = newLockAmount * currentPowerRatio

      potentialIncreasedNft = scaleApp.Lock.Factory.createFromNft({
        id: nft.id,
        lockAmount: newLockAmount,
        lockEnds: nft.lockEnds,
        lockValue: newLockValue,
      })
    }
  }

  // Now we can do conditional returns after all hooks are called
  if (!governanceToken) {
    return null
  }

  if (!nft || !nftId) {
    return (
      <PageContainer>
        <p>not found</p>
      </PageContainer>
    )
  }

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[0]

  if (!complexTab) {
    return null
  }

  const title = `Increase`

  // Pass ILock directly - TypeScript ensures type safety via Pick<ILock>
  const currentLockDisplayData = nft
  const futureLockDisplayData = potentialIncreasedNft

  // Check if user has sufficient balance (based on amountToAddWei, not input amount)
  // This uses immediate amount for button state
  let hasSufficientBalance = true
  if (governanceToken && amountToAddWei > 0n) {
    hasSufficientBalance = amountToAddWei <= governanceToken.balance
  }

  // Calculate debounced amountToAddWei for error validation only
  // This prevents showing errors while user is still typing
  // Use lockAmountRaw directly for exact BigInt precision
  let debouncedAmountToAddWei = 0n
  if (mode === "target" && governanceToken && debouncedAmount && Number(debouncedAmount) > 0) {
    try {
      const targetAmountWei = governanceToken.parseUnits(debouncedAmount)
      const currentLockAmountWei = nft?.lockAmountRaw ?? 0n
      debouncedAmountToAddWei = targetAmountWei >= currentLockAmountWei ? targetAmountWei - currentLockAmountWei : 0n
    } catch {
      debouncedAmountToAddWei = 0n
    }
  }

  // Validation: in target mode, ensure target >= current (only show error if target < current)
  // Use debouncedAmount for validation to avoid showing errors while typing
  // Use lockAmountRaw directly for exact BigInt precision
  let isValidTarget = true
  if (mode === "target" && governanceToken && debouncedAmount && Number(debouncedAmount) > 0) {
    try {
      const targetAmountWei = governanceToken.parseUnits(debouncedAmount)
      const currentLockAmountWei = nft?.lockAmountRaw ?? 0n
      // Only invalid if target is less than current (allow equal or greater)
      isValidTarget = targetAmountWei >= currentLockAmountWei
    } catch {
      isValidTarget = false
    }
  }

  // Check debounced balance for error display
  let hasDebouncedSufficientBalance = true
  if (governanceToken && debouncedAmountToAddWei > 0n) {
    hasDebouncedSufficientBalance = debouncedAmountToAddWei <= governanceToken.balance
  }

  // Calculate external error for TokenInputCard (target mode only)
  // Use debouncedAmount for error display to avoid showing errors while typing
  // Priority: invalid target > insufficient balance
  let externalError: { message: string } | null = null
  if (mode === "target" && governanceToken && debouncedAmount && Number(debouncedAmount) > 0) {
    if (!isValidTarget) {
      externalError = { message: "Target amount must be greater than current locked amount" }
    } else if (!hasDebouncedSufficientBalance) {
      externalError = { message: "Insufficient balance" }
    }
  }

  const canIncreaseLockAmount = amountToAddWei > 0n && !nft?.hasExpired && hasSufficientBalance && isValidTarget

  const onIncreaseLockAmountClick = async (nft?: ILock) => {
    if (!account.address || !nft || amountToAddWei === 0n) return

    const onChainActions = new IncreaseLockAmountActionsAdapter()

    const result = await increaseLockAmountInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      nft: {
        ...nft,
        lockAmount: Number(nft.lockAmount),
        lockValue: Number(nft.lockValue),
      },
      amount: amountToAddWei, // Use calculated BigInt amount
      vestedToken,
      governanceToken,
      manageLockLens,
      approvalMode: scaleApp?.Settings.approvalMode,
      contractRiskLevel: "immutable",
      emitter,
      eventBus: scaleApp.EventBus,
      onChainActions,
    })

    if (result) {
      // Orchestrator will automatically refresh via event listener
    }
  }

  // Clear amount when mode changes
  const handleModeChange = (checked: boolean) => {
    setMode(checked ? "target" : "increase")
    setAmount("")
    setDebouncedAmount("")
  }

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          {currentLockDisplayData && (
            <>
              {mode === "increase" && (
                <IncreaseAmountCard
                  currentLock={currentLockDisplayData}
                  futureLock={futureLockDisplayData}
                  amount={amount}
                  governanceToken={governanceToken}
                  hasExpired={nft?.hasExpired ?? false}
                  canIncrease={canIncreaseLockAmount}
                  icon={complexTab.icon ? <complexTab.icon /> : undefined}
                  onModeChange={handleModeChange}
                  onAmountChange={setAmount}
                  onIncrease={() => onIncreaseLockAmountClick(nft ?? undefined)}
                />
              )}
              {mode === "target" && (
                <TargetAmountCard
                  currentLock={currentLockDisplayData}
                  amount={amount}
                  governanceToken={governanceToken}
                  hasExpired={nft?.hasExpired ?? false}
                  canIncrease={canIncreaseLockAmount}
                  icon={complexTab.icon ? <complexTab.icon /> : undefined}
                  amountToAdd={amountToAdd}
                  targetAmount={targetAmount}
                  isValidTarget={isValidTarget}
                  externalError={externalError}
                  onModeChange={handleModeChange}
                  onAmountChange={setAmount}
                  onIncrease={() => onIncreaseLockAmountClick(nft ?? undefined)}
                />
              )}
            </>
          )}
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})

export default IncreaseLockAmount
